import React, { useState, useEffect } from "react";
import {
  Fab,
  TextField,
  Container,
  Typography,
  Tooltip,
  Grid,
  Box,
  Paper,
  InputAdornment,
  CircularProgress,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import { Add, Search, Code } from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import SnippetCard from "../components/SnippetCard";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Create theme based on dark mode preference
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [darkMode]
  );

  useEffect(() => {
    // Fetch snippets from backend
    const fetchSnippets = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/snippets`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        const data = await response.json();

        if (data.message === "Invalid token") {
          // Remove user from local storage
          dispatch(logout());
          return;
        }

        let filteredSnippets = data.snippets.filter(
          (snippet) => snippet.username !== user.username
        );

        setSnippets(filteredSnippets);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching snippets:", error);
        setError("Failed to load snippets. Please try again later.");
        setLoading(false);
      }
    };

    fetchSnippets();
  }, [dispatch, user.token, user.username]);

  // Filter snippets based on search input
  const filteredSnippets = snippets.filter(
    (snippet) =>
      snippet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      snippet.language.toLowerCase().includes(searchQuery.toLowerCase()) ||
      snippet.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const containerStyles = {
    maxWidth: { sm: "sm", md: "md", lg: "lg", xl: "xl" },
    px: { xs: 2, sm: 3 },
    mx: "auto",
  };

  const headerStyles = {
    mb: { xs: 2, sm: 3 },
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 2, sm: 0 },
    justifyContent: "space-between",
    alignItems: { xs: "stretch", sm: "center" },
  };

  const searchBarStyles = {
    p: { xs: 1.5, sm: 2 },
    mb: { xs: 2, sm: 3 },
    borderRadius: 2,
    transition: "all 0.3s",
    "&:hover": {
      boxShadow: (theme) => theme.shadows[4],
    },
  };

  const fabStyles = {
    position: "fixed",
    bottom: { xs: 16, sm: 24 },
    right: { xs: 16, sm: 24 },
    transition: "all 0.3s",
    "&:hover": {
      transform: "scale(1.1)",
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            transition: "all 0.3s",
            bgcolor: "background.default",
          }}
        >
          <Container sx={containerStyles}>
            {/* Header */}
            <Box sx={headerStyles}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Code sx={{ fontSize: 32, mr: 2, color: "primary.main" }} />
                <Typography variant="h4" component="h1" fontWeight="bold">
                  Community Snippets
                </Typography>
              </Box>
            </Box>

            {/* Search Bar */}
            <Paper elevation={3} sx={searchBarStyles}>
              <TextField
                fullWidth
                placeholder="Search by Title, Language, Tags"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: "background.paper" }}
              />
            </Paper>

            {/* Main Content */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
              </Box>
            ) : filteredSnippets.length > 0 ? (
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {filteredSnippets.map((snippet) => (
                  <Grid item xs={12} sm={6} lg={4} key={snippet._id}>
                    <SnippetCard
                      snippet={snippet}
                      subHeaderTitle={"Created By"}
                      sx={{
                        height: "100%",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  my: 10,
                }}
              >
                <Paper
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 5,
                    borderRadius: 2,
                    maxWidth: 500,
                  }}
                >
                  <Code sx={{ fontSize: 80, mb: 2, color: "text.secondary" }} />
                  <Typography variant="h5" gutterBottom fontWeight="medium">
                    No Snippets Found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                  >
                    {searchQuery
                      ? "Try adjusting your search terms"
                      : "Be the first to share a code snippet!"}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Container>

          {/* Floating Add Button */}
          <Tooltip title="Add Snippet" placement="left">
            <Fab
              color="primary"
              aria-label="add"
              sx={fabStyles}
              onClick={() => navigate("/add-snippet")}
            >
              <Add />
            </Fab>
          </Tooltip>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;
